import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Divider,
  Modal,
  Paper,
  TextField,
  Snackbar,
  Alert,
  Typography,
} from '@mui/material';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import canvasLogo from '../assets/canvas-logo.png';
import { fetchIntegrationConfigs } from '../redux/userSlice';
import * as amplitude from '@amplitude/analytics-browser';
import { fetchAuthSession } from '@aws-amplify/auth';
import config from '../utils/Constants';
import { Label } from '@aws-amplify/ui-react';
import ApiKeyCard from './ApiKeyCard';

const PLACEHOLDER_SECRET = '••••••• (saved)';

function SectionHeader({ icon, title, description }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3 }}>
      <Box sx={{ color: 'primary.main', mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Settings() {
  const [open, setOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const integrationConfigs = useSelector((state) => state.user.integrationConfigs);

  const [credentials, setCredentials] = useState({
    canvasAuthApiUrl: '',
    canvasFhirApiUrl: '',
    canvasClientId: '',
    canvasClientSecret: '',
    awsRegion: '',
    awsS3Bucket: '',
    awsAccessKey: '',
    awsSecretKey: '',
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [errors, setErrors] = useState({});

  const integrations = [
    {
      id: 1,
      logo: canvasLogo,
      title: 'Canvas EMR',
      integrationKey: 'canvas',
      description: 'Open-source Electronic Medical Record system.',
    },
    {
      id: 2,
      logo: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg',
      title: 'AWS S3',
      integrationKey: 'aws_s3',
      description: 'Amazon S3 for storing synthetic medical data.',
    },
  ];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchIntegrationConfigs());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedIntegration) return;
    const key = selectedIntegration.integrationKey;
    const stored = integrationConfigs[key];
    if (selectedIntegration.title === 'Canvas EMR') {
      setCredentials((prev) => ({
        ...prev,
        canvasAuthApiUrl: stored?.config?.canvasAuthApiUrl || '',
        canvasFhirApiUrl: stored?.config?.canvasFhirApiUrl || '',
        canvasClientId: stored?.config?.canvasClientId || '',
        canvasClientSecret: stored?.configured ? PLACEHOLDER_SECRET : '',
      }));
    } else if (selectedIntegration.title === 'AWS S3') {
      setCredentials((prev) => ({
        ...prev,
        awsRegion: stored?.config?.awsRegion || '',
        awsS3Bucket: stored?.config?.awsS3Bucket || '',
        awsAccessKey: stored?.configured ? PLACEHOLDER_SECRET : '',
        awsSecretKey: stored?.configured ? PLACEHOLDER_SECRET : '',
      }));
    }
  }, [selectedIntegration, integrationConfigs]);

  const handleOpen = (integration) => {
    setSelectedIntegration(integration);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedIntegration(null);
    setErrors({});
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    const key = selectedIntegration?.integrationKey;
    const apiUrl = config.getDynamicApiUrl();
    let nonSensitive = {};
    let sensitiveFields = {};

    if (selectedIntegration?.title === 'Canvas EMR') {
      if (!credentials.canvasAuthApiUrl || !credentials.canvasFhirApiUrl || !credentials.canvasClientId) {
        setAlertMessage('Error: Please fill in all required fields.');
        setAlertSeverity('error');
        setAlertOpen(true);
        return;
      }
      nonSensitive = {
        canvasAuthApiUrl: credentials.canvasAuthApiUrl,
        canvasFhirApiUrl: credentials.canvasFhirApiUrl,
        canvasClientId: credentials.canvasClientId,
      };
      if (credentials.canvasClientSecret && credentials.canvasClientSecret !== PLACEHOLDER_SECRET) {
        sensitiveFields = { canvasClientSecret: credentials.canvasClientSecret };
      }
    } else if (selectedIntegration?.title === 'AWS S3') {
      if (!credentials.awsS3Bucket) {
        setErrors({ awsS3Bucket: 'S3 Bucket name is required.' });
        setAlertMessage('Error: Please fix the highlighted fields.');
        setAlertSeverity('error');
        setAlertOpen(true);
        return;
      }
      nonSensitive = {
        awsRegion: credentials.awsRegion || 'us-east-1',
        awsS3Bucket: credentials.awsS3Bucket,
      };
      if (credentials.awsAccessKey && credentials.awsAccessKey !== PLACEHOLDER_SECRET) {
        sensitiveFields.awsAccessKey = credentials.awsAccessKey;
      }
      if (credentials.awsSecretKey && credentials.awsSecretKey !== PLACEHOLDER_SECRET) {
        sensitiveFields.awsSecretKey = credentials.awsSecretKey;
      }
    }

    try {
      const session = await fetchAuthSession();
      const token = session.tokens.accessToken.toString();
      const response = await fetch(`${apiUrl}/api/v1/integrations/${key}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ config: nonSensitive, secrets: sensitiveFields }),
      });
      if (response.ok) {
        setAlertMessage('Integration saved successfully!');
        setAlertSeverity('success');
        dispatch(fetchIntegrationConfigs());
      } else {
        const err = await response.text();
        setAlertMessage(`Error saving integration: ${err}`);
        setAlertSeverity('error');
      }
    } catch (error) {
      setAlertMessage('Error saving integration.');
      setAlertSeverity('error');
    }
    setAlertOpen(true);
    handleClose();
  };

  const handleAnalyticsClick = () => {
    amplitude.track('another integration clicked', {});
    setAlertMessage('Thank you for your interest! We will reach out to you soon.');
    setAlertSeverity('success');
    setAlertOpen(true);
  };

  return (
    <Box sx={{ maxWidth: 860, mx: 'auto', py: 1 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Settings
      </Typography>

      {/* ── API Access ─────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <ApiKeyCard />
      </Paper>

      {/* ── Outbound Integrations ───────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <SectionHeader
          icon={<IntegrationInstructionsIcon />}
          title="Outbound Integrations"
          description="Configure destinations where Cynthia will push synthetic data."
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {integrations.map((integration) => {
            const configured = integrationConfigs[integration.integrationKey]?.configured;
            return (
              <Paper
                key={integration.id}
                variant="outlined"
                sx={{
                  flex: '1 1 220px',
                  maxWidth: 260,
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  borderColor: configured ? 'success.light' : 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
                }}
                onClick={() => handleOpen(integration)}
              >
                <img
                  src={integration.logo}
                  alt={`${integration.title} logo`}
                  style={{ width: '100%', maxHeight: 40, objectFit: 'contain' }}
                />
                <Typography variant="subtitle2" fontWeight={700} textAlign="center">
                  {integration.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  {integration.description}
                </Typography>
                {configured && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                    ✓ Configured
                  </Typography>
                )}
              </Paper>
            );
          })}

          <Paper
            variant="outlined"
            sx={{
              flex: '1 1 220px',
              maxWidth: 260,
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              cursor: 'pointer',
              borderStyle: 'dashed',
              color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
            onClick={handleAnalyticsClick}
          >
            <Typography variant="subtitle2" fontWeight={600} textAlign="center">
              + Request an integration
            </Typography>
            <Typography variant="caption" textAlign="center">
              Let us know what you need
            </Typography>
          </Paper>
        </Box>
      </Paper>

      {/* ── Integration credentials modal ──────────────────────────────────── */}
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 440,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            {selectedIntegration?.title} Credentials
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {selectedIntegration?.title === 'Canvas EMR' && (
            <>
              <TextField name="canvasAuthApiUrl" label="Auth URL" value={credentials.canvasAuthApiUrl} onChange={handleChange} fullWidth margin="normal" />
              <TextField name="canvasFhirApiUrl" label="FHIR URL" value={credentials.canvasFhirApiUrl} onChange={handleChange} fullWidth margin="normal" />
              <TextField name="canvasClientId" label="Client ID" value={credentials.canvasClientId} onChange={handleChange} fullWidth margin="normal" />
              <TextField name="canvasClientSecret" label="Client Secret" type="password" value={credentials.canvasClientSecret} onChange={handleChange} fullWidth margin="normal" placeholder={integrationConfigs['canvas']?.configured ? PLACEHOLDER_SECRET : ''} />
            </>
          )}
          {selectedIntegration?.title === 'AWS S3' && (
            <>
              <TextField name="awsRegion" label="Region" placeholder="us-east-1" value={credentials.awsRegion} onChange={handleChange} fullWidth margin="normal" />
              <TextField name="awsS3Bucket" label="S3 Bucket" placeholder="my-bucket-name" value={credentials.awsS3Bucket} onChange={handleChange} fullWidth margin="normal" error={!!errors.awsS3Bucket} helperText={errors.awsS3Bucket} />
              <TextField name="awsAccessKey" label="Access Key" value={credentials.awsAccessKey} onChange={handleChange} fullWidth margin="normal" placeholder={integrationConfigs['aws_s3']?.configured ? PLACEHOLDER_SECRET : ''} />
              <TextField name="awsSecretKey" label="Secret Key" type="password" value={credentials.awsSecretKey} onChange={handleChange} fullWidth margin="normal" placeholder={integrationConfigs['aws_s3']?.configured ? PLACEHOLDER_SECRET : ''} />
            </>
          )}

          <Button onClick={handleSubmit} variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
            Save
          </Button>
        </Box>
      </Modal>

      <Snackbar open={alertOpen} autoHideDuration={3000} onClose={() => setAlertOpen(false)}>
        <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
